import { z } from "zod";

// M13 — internal company-commission fields on a ticket line. Shared by the
// proforma and invoice create forms. The block is optional; when enabled,
// supplier and HT are required while 0% remains a valid commission rate. The
// server enforces the same rule. The ticket's own `segment` field is separate
// and always required — it is not part of this block.
export const companyCommissionSchema = z
  .object({
    enabled: z.boolean().optional().default(false),
    supplierName: z.string().optional().default(""),
    priceHt: z.coerce.number().min(0, "Le prix HT doit être positif.").optional().default(0),
    commissionPct: z.coerce.number().min(0, "Le pourcentage doit être positif.").optional().default(0),
  })
  .optional()
  .superRefine((cc, ctx) => {
    if (!cc) return;
    const anyFilled =
      cc.supplierName.trim() !== "" ||
      (cc.priceHt ?? 0) > 0 ||
      (cc.commissionPct ?? 0) > 0;
    const enabled = cc.enabled ?? anyFilled;
    if (!enabled) return;

    if (!cc.supplierName.trim())
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Renseignez l'agence / le fournisseur.", path: ["supplierName"] });
    if (!((cc.priceHt ?? 0) > 0))
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Renseignez le prix HT.", path: ["priceHt"] });
  });

// Drop disabled company-commission state so the server receives `undefined`
// rather than client-only toggle data or a row of zeros/empties.
export function stripEmptyCompanyCommission<
  T extends { enabled?: boolean; supplierName?: string; priceHt?: number; commissionPct?: number },
>(cc: T | undefined): Omit<T, "enabled"> | undefined {
  if (!cc) return undefined;
  const anyFilled =
    (cc.supplierName ?? "").trim() !== "" ||
    (cc.priceHt ?? 0) > 0 ||
    (cc.commissionPct ?? 0) > 0;
  const enabled = cc.enabled ?? anyFilled;
  if (!enabled) return undefined;
  const { enabled: _enabled, ...payload } = cc;
  return payload;
}
